/* ===================================================================
   PUBLIC review lander -- /r/<embed_key>  (Brad, 2026-07-06, audit C3).

   This is the destination of every printed QR code, NFC tap card, and
   email-signature review link the Collection screen hands out
   (https://app.rhodyreviews.com/r/<embed_key>). Before this existed the
   SPA's catch-all bounced a scanning customer to /dashboard -> the LOGIN
   screen: a silent, total dead-end of the whole collection loop.

   PUBLIC + UNAUTHENTICATED by design: a business's CUSTOMER lands here,
   not the owner. Data comes from the engine's existing public widget
   endpoint GET /w/<embed_key>/data.json (same public credential the
   embeddable widget uses; nothing secret in the payload). The payload's
   review_url is the business's REAL Google writereview URL, derived
   server-side from the linked Google place -- the exact same link the
   Request Agent already sends by email.

   BEHAVIOR:
     - review target known  -> auto-forward straight to the Google
       leave-a-review page (the printed QR should feel like "scan ->
       Google"), with a visible button as the fallback if the redirect
       is blocked or slow.
     - target not set up    -> say so honestly. No dead button, no fake
       numbers, nothing fabricated.
     - unknown/invalid key  -> honest "link not active" message.

   HARD RULES: no score-gating and no rating pre-screen of any kind on
   this page -- every visitor gets the same public Google link. No
   fabricated stats (the rating/count shown come straight from the
   public payload of real reviews).
   =================================================================== */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE } from '../lib/api/_http';

interface PublicWidgetPayload {
  error?: string;
  business_name?: string;
  avg_rating?: number;
  total_reviews?: number;
  review_url?: string;
  powered_by?: boolean;
}

type LanderState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'ready'; data: PublicWidgetPayload };

const wrap: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
  background: '#f6f8fa',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: '#202124',
};

const card: React.CSSProperties = {
  width: '100%',
  maxWidth: 420,
  background: '#fff',
  border: '1px solid #e7e9ee',
  borderRadius: 16,
  padding: '34px 26px',
  textAlign: 'center',
  boxShadow: '0 2px 10px rgba(16,24,40,.06)',
};

const btn: React.CSSProperties = {
  display: 'inline-block',
  marginTop: 18,
  padding: '13px 26px',
  borderRadius: 999,
  background: '#1a73e8',
  color: '#fff',
  fontSize: 15,
  fontWeight: 700,
  textDecoration: 'none',
  border: 0,
  cursor: 'pointer',
};

function GoldStars({ n }: { n: number }) {
  return (
    <span aria-hidden="true" style={{ display: 'inline-flex', gap: 2 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} style={{ color: i < n ? '#fbbc04' : '#dadce0', fontSize: 20, lineHeight: 1 }}>
          ★
        </span>
      ))}
    </span>
  );
}

export function ReviewLander() {
  const { key } = useParams<{ key: string }>();
  const [state, setState] = useState<LanderState>({ phase: 'loading' });

  useEffect(() => {
    if (!key) {
      setState({ phase: 'error' });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/w/${encodeURIComponent(key)}/data.json`,
          { headers: { Accept: 'application/json' } },
        );
        const data = (await res.json()) as PublicWidgetPayload;
        if (cancelled) return;
        if (!res.ok || data.error === 'unknown_widget') {
          setState({ phase: 'error' });
          return;
        }
        setState({ phase: 'ready', data });
        // Auto-forward to the real Google leave-a-review page. The lander
        // stays rendered underneath (with the same link as a button) in
        // case the redirect is blocked or slow.
        if (data.review_url) {
          window.location.replace(data.review_url);
        }
      } catch {
        if (!cancelled) setState({ phase: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [key]);

  if (state.phase === 'loading') {
    return (
      <div style={wrap}>
        <div style={card}>
          <p style={{ margin: 0, fontSize: 15, color: '#5f6368' }}>Loading your review link...</p>
        </div>
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div style={wrap}>
        <div style={card}>
          <h1 style={{ margin: '0 0 10px', fontSize: 20 }}>This review link isn't active</h1>
          <p style={{ margin: 0, fontSize: 14, color: '#5f6368', lineHeight: 1.5 }}>
            The link may have been mistyped or is no longer in use. If a business gave
            it to you, let them know it isn't working.
          </p>
        </div>
      </div>
    );
  }

  const { data } = state;
  const biz = (data.business_name || '').trim();
  const hasTarget = Boolean(data.review_url);
  const rating = typeof data.avg_rating === 'number' && data.avg_rating > 0 ? data.avg_rating : null;
  const total = typeof data.total_reviews === 'number' ? data.total_reviews : 0;

  return (
    <div style={wrap}>
      <div style={card}>
        {rating != null && total > 0 && (
          <div style={{ marginBottom: 14 }}>
            <GoldStars n={Math.round(rating)} />
            <div style={{ fontSize: 12.5, color: '#5f6368', marginTop: 4 }}>
              {rating.toFixed(1)} from {total.toLocaleString()} review{total === 1 ? '' : 's'}
            </div>
          </div>
        )}
        <h1 style={{ margin: '0 0 10px', fontSize: 21, letterSpacing: '-.01em' }}>
          {biz ? `Share your experience with ${biz}` : 'Share your experience'}
        </h1>
        {hasTarget ? (
          <>
            <p style={{ margin: 0, fontSize: 14, color: '#5f6368', lineHeight: 1.5 }}>
              Taking you to Google now. If nothing happens, use the button below.
              It takes about 30 seconds.
            </p>
            <a style={btn} href={data.review_url} rel="noopener noreferrer">
              Leave a Google review
            </a>
          </>
        ) : (
          <p style={{ margin: 0, fontSize: 14, color: '#5f6368', lineHeight: 1.5 }}>
            {biz ? `${biz} hasn't` : "This business hasn't"} finished setting up its
            review link yet. Please check back soon.
          </p>
        )}
        {data.powered_by !== false && (
          <div style={{ marginTop: 24 }}>
            <a
              href="https://owntheboxmarketing.com/reviews/"
              title="Rhody Reviews by Own The Box Marketing"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 11.5, color: '#9aa0a6', textDecoration: 'none' }}
            >
              Powered by Rhody Reviews
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReviewLander;
